// @givmo/board — the annual disclosures' PDFs: one response (its clean copy or its changes), every
// response of a year, and a printable paper form (CBG-YR2-WEB1/WEB2).
//
// Each PDF is DRAWN by the backend from the recorded answers and never stored (contract §9), so a
// download is a read like any other: the generated operation answers `application/pdf`, the client
// parses it as a blob, and the browser is handed the bytes. Times print in the reader's own zone
// (`time_zone`, an IANA name the backend reads; it falls back to UTC, labelled, for one it can't).

/** The reader's IANA zone, or undefined where the browser won't say. */
export function readerTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone.length > 0 && zone.length <= 64 ? zone : undefined;
  } catch {
    return undefined;
  }
}

/** A filename a person and a filesystem will both accept. */
export function pdfName(...parts: (string | null | undefined)[]): string {
  const words = parts
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .map((part) =>
      part
        .normalize('NFKD')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .toLowerCase(),
    )
    .filter((part) => part.length > 0);
  return `${words.join('-') || 'disclosure'}.pdf`;
}

/** Hand the bytes to the browser as a download, the shape `export-download.ts` established. */
export function savePdf(data: unknown, filename: string): void {
  const blob =
    data instanceof Blob ? data : new Blob([data as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Release on the next tick — revoking synchronously can race the click in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
